import React from "react";
import {
  Brain,
  Target,
  MapPin,
  ThumbsUp,
  HelpCircle,
  Wrench,
  Clock,
  Camera,
  FileText,
  CheckCircle,
  Activity,
} from "lucide-react";

export type AgentMessageType =
  | "starting"
  | "task"
  | "step"
  | "eval"
  | "memory"
  | "goal"
  | "action"
  | "wait"
  | "screenshot"
  | "result"
  | "completed"
  | "tokens"
  | "telemetry"
  | "generic";

export interface ParsedAgentMessage {
  type: AgentMessageType;
  content: string;
  rawContent: string;
  metadata?: {
    stepNumber?: number;
    actionNumber?: string;
    evalStatus?: "Success" | "Unknown" | "Failed";
    waitSeconds?: number;
    tokenCount?: number;
    jsonData?: any;
  };
}

export function parseAgentMessage(log: string): ParsedAgentMessage {
  const cleaned = log
    .replace(/^INFO\s+/, "")
    .replace(/^\[agent\]\s*/, "")
    .trim();

  // Starting agent
  if (cleaned.includes("🧠 Starting a browser-use agent")) {
    return {
      type: "starting",
      content: cleaned.replace("🧠 ", ""),
      rawContent: log,
    };
  }

  // Task
  if (cleaned.includes("🚀 Starting task:")) {
    return {
      type: "task",
      content: cleaned.replace("🚀 Starting task: ", ""),
      rawContent: log,
    };
  }

  // Step
  if (cleaned.includes("📍 Step")) {
    const stepMatch = cleaned.match(/📍 Step (\d+)/);
    return {
      type: "step",
      content: cleaned,
      rawContent: log,
      metadata: {
        stepNumber: stepMatch ? parseInt(stepMatch[1]) : undefined,
      },
    };
  }

  // Evaluation
  if (cleaned.includes("Eval:")) {
    const evalMatch = cleaned.match(
      /(👍|🤷|❌)\s*Eval:\s*(Success|Unknown|Failed)\s*-\s*(.+)/,
    );
    return {
      type: "eval",
      content: evalMatch
        ? evalMatch[3]
        : cleaned.replace(/^(👍|🤷|❌)\s*Eval:\s*/, ""),
      rawContent: log,
      metadata: {
        evalStatus: evalMatch
          ? (evalMatch[2] as "Success" | "Unknown" | "Failed")
          : "Unknown",
      },
    };
  }

  // Memory
  if (cleaned.includes("🧠 Memory:")) {
    return {
      type: "memory",
      content: cleaned.replace("🧠 Memory: ", ""),
      rawContent: log,
    };
  }

  // Goal
  if (cleaned.includes("🎯 Next goal:")) {
    return {
      type: "goal",
      content: cleaned.replace("🎯 Next goal: ", ""),
      rawContent: log,
    };
  }

  // Action
  if (cleaned.includes("🛠️  Action")) {
    const actionMatch = cleaned.match(/🛠️\s+Action\s+([^:]+):\s*(.+)/);
    let jsonData;
    try {
      const jsonMatch = cleaned.match(/({.+})/);
      if (jsonMatch) {
        jsonData = JSON.parse(jsonMatch[1]);
      }
    } catch (e) {
      // Ignore JSON parse errors
    }

    return {
      type: "action",
      content: cleaned,
      rawContent: log,
      metadata: {
        actionNumber: actionMatch ? actionMatch[1] : undefined,
        jsonData,
      },
    };
  }

  // Wait
  if (cleaned.includes("Waited") && cleaned.includes("seconds")) {
    const waitMatch = cleaned.match(/Waited (\d+) seconds/);
    return {
      type: "wait",
      content: cleaned,
      rawContent: log,
      metadata: {
        waitSeconds: waitMatch ? parseInt(waitMatch[1]) : undefined,
      },
    };
  }

  // Screenshot
  if (cleaned.includes("Screenshot taken")) {
    return {
      type: "screenshot",
      content: cleaned,
      rawContent: log,
    };
  }

  // Result
  if (cleaned.includes("📄 Result:")) {
    let jsonData;
    try {
      const jsonMatch = cleaned.match(/📄 Result:\s*(.+)/);
      if (jsonMatch) {
        jsonData = JSON.parse(jsonMatch[1]);
      }
    } catch (e) {
      // Ignore JSON parse errors
    }

    return {
      type: "result",
      content: cleaned.replace("📄 Result: ", ""),
      rawContent: log,
      metadata: {
        jsonData,
      },
    };
  }

  // Completed
  if (
    cleaned.includes("✅ Task completed") ||
    cleaned.includes("✅ Successfully")
  ) {
    return {
      type: "completed",
      content: cleaned,
      rawContent: log,
    };
  }

  // Tokens
  if (cleaned.includes("📝 Total input tokens used")) {
    const tokenMatch = cleaned.match(
      /📝 Total input tokens used \(approximate\):\s*(\d+)/,
    );
    return {
      type: "tokens",
      content: cleaned,
      rawContent: log,
      metadata: {
        tokenCount: tokenMatch ? parseInt(tokenMatch[1]) : undefined,
      },
    };
  }

  // Telemetry
  if (cleaned.includes("Agent run telemetry logged")) {
    return {
      type: "telemetry",
      content: cleaned,
      rawContent: log,
    };
  }

  // Generic fallback
  return {
    type: "generic",
    content: cleaned,
    rawContent: log,
  };
}

interface AgentMessageProps {
  message: ParsedAgentMessage;
  useColoredHeaders?: boolean; // Toggle for colored vs gray headers
}

const AgentMessage: React.FC<AgentMessageProps> = ({
  message,
  useColoredHeaders = true,
}) => {
  const getIconColorClass = () => {
    if (!useColoredHeaders) return "text-gray-500";

    switch (message.type) {
      case "starting":
      case "memory":
        return "text-purple-600";
      case "task":
      case "goal":
        return "text-blue-600";
      case "step":
        return "text-indigo-600";
      case "eval":
        const status = message.metadata?.evalStatus;
        if (status === "Success") return "text-green-600";
        if (status === "Failed") return "text-red-600";
        return "text-yellow-600";
      case "action":
        return "text-orange-600";
      case "wait":
        return "text-gray-600";
      case "screenshot":
        return "text-teal-600";
      case "result":
      case "completed":
        return "text-green-600";
      case "tokens":
        return "text-blue-500";
      default:
        return "text-gray-500";
    }
  };

  const getHeaderTextClass = () => {
    return useColoredHeaders ? getIconColorClass() : "text-gray-600";
  };

  const renderIcon = () => {
    const colorClass = getIconColorClass();

    switch (message.type) {
      case "starting":
        return <Brain size={16} className={colorClass} />;
      case "task":
        return <Target size={16} className={colorClass} />;
      case "step":
        return <MapPin size={16} className={colorClass} />;
      case "eval":
        return <ThumbsUp size={16} className={colorClass} />;
      case "memory":
        return <Brain size={16} className={colorClass} />;
      case "goal":
        return <Target size={16} className={colorClass} />;
      case "action":
        return <Wrench size={16} className={colorClass} />;
      case "wait":
        return <Clock size={16} className={colorClass} />;
      case "screenshot":
        return <Camera size={16} className={colorClass} />;
      case "result":
        return <FileText size={16} className={colorClass} />;
      case "completed":
        return <CheckCircle size={16} className={colorClass} />;
      case "tokens":
        return <Activity size={16} className={colorClass} />;
      default:
        return <Activity size={16} className={colorClass} />;
    }
  };

  const renderContent = () => {
    switch (message.type) {
      case "starting":
        return (
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Starting Agent
              </span>
            </div>
            <p className="text-sm text-black">{message.content}</p>
          </div>
        );

      case "task":
        return (
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Task
              </span>
            </div>
            <p className="text-sm text-black ">{message.content}</p>
          </div>
        );

      case "step":
        return (
          <div>
            <div className="flex items-center space-x-2">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Step {message.metadata?.stepNumber}
              </span>
            </div>
          </div>
        );

      case "eval":
        const status = message.metadata?.evalStatus;
        return (
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                {status}
              </span>
            </div>
            <p className="text-sm text-black">{message.content}</p>
          </div>
        );

      case "memory":
        return (
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Memory
              </span>
            </div>
            <p className="text-sm text-black">{message.content}</p>
          </div>
        );

      case "goal":
        return (
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Next Goal
              </span>
            </div>
            <p className="text-sm text-black">{message.content}</p>
          </div>
        );

      case "action":
        return (
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Action {message.metadata?.actionNumber}
              </span>
            </div>
            {message.metadata?.jsonData ? (
              <div className=" rounded p-2 mt-2 border border-gray-200">
                <pre className="text-xs  font-mono overflow-x-auto">
                  {JSON.stringify(message.metadata.jsonData, null, 2)}
                </pre>
              </div>
            ) : (
              <p className="text-sm text-black">{message.content}</p>
            )}
          </div>
        );

      case "wait":
        return (
          <div>
            <div className="flex items-center space-x-2">
              {renderIcon()}
              <span className="text-sm text-black">
                Waited {message.metadata?.waitSeconds} seconds
              </span>
            </div>
          </div>
        );

      case "screenshot":
        return (
          <div>
            <div className="flex items-center space-x-2">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Screenshot taken
              </span>
            </div>
          </div>
        );

      case "result":
        return (
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                Result
              </span>
            </div>
            {message.metadata?.jsonData ? (
              <div className=" rounded p-3 mt-2 border bg-green-50 border-green-200">
                <pre className="text-xs  font-mono text-green-800 overflow-x-auto">
                  {JSON.stringify(message.metadata.jsonData, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="bg-gray-800 rounded p-3 mt-2">
                <pre className="text-xs text-green-400 font-mono overflow-x-auto">
                  {message.content}
                </pre>
              </div>
            )}
          </div>
        );

      case "completed":
        return (
          <div>
            <div className="flex items-center space-x-2">
              {renderIcon()}
              <span className={`text-sm font-medium ${getHeaderTextClass()}`}>
                ✅ Task completed successfully!
              </span>
            </div>
          </div>
        );

      case "tokens":
        return (
          <div>
            <div className="flex items-center space-x-2">
              {renderIcon()}
              <span className="text-sm text-black">
                Total tokens used:{" "}
                {message.metadata?.tokenCount?.toLocaleString()}
              </span>
            </div>
          </div>
        );

      default:
        return (
          <div>
            <div className="flex items-center space-x-2">
              {renderIcon()}
              <span className="text-xs font-mono text-black">
                {message.content}
              </span>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex justify-start">
      <div className="w-full">{renderContent()}</div>
    </div>
  );
};

export default AgentMessage;
